"use client";

import { Rocket, Target } from "lucide-react";
import { AnimatedSectionHeader } from "@/components/typography/AnimatedSectionHeader";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import type { AboutPurposeContent } from "@/lib/api-types";
import { cn } from "@/lib/utils";

interface AboutPurposeSectionProps {
  purpose: AboutPurposeContent;
}

export function AboutPurposeSection({ purpose }: AboutPurposeSectionProps) {
  const purposeCards = [
    {
      data: purpose.vision,
      icon: Target,
      accent: "border-t-[var(--site-accent)]",
      tint: "bg-[var(--site-primary)]/5",
      iconBg: "bg-[var(--site-accent)]/15 text-[var(--site-accent)]",
    },
    {
      data: purpose.mission,
      icon: Rocket,
      accent: "border-t-[var(--site-primary)]",
      tint: "bg-[var(--site-surface)]",
      iconBg: "bg-[var(--site-primary)]/10 text-[var(--site-primary)]",
    },
  ] as const;

  return (
    <section className="site-section bg-[var(--site-surface)]">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <AnimatedSectionHeader
            eyebrow="Purpose"
            kineticTrigger="mount"
            title="Vision & Mission"
            subtitle="The principles that guide our work in every community we serve."
          />
        </Reveal>
        <Stagger className="grid gap-6 md:grid-cols-2">
          {purposeCards.map(({ data, icon: Icon, accent, tint, iconBg }) => (
            <StaggerItem key={data.title}>
              <article
                className={cn(
                  "about-purpose-card site-card h-full border-t-4 p-6 md:p-8",
                  accent,
                  tint
                )}
              >
                <div
                  className={cn(
                    "mb-5 flex h-12 w-12 items-center justify-center rounded-full",
                    iconBg
                  )}
                >
                  <Icon size={22} aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-xl font-bold text-[var(--site-primary)] md:text-2xl">
                  {data.title}
                </h3>
                <p className="leading-relaxed text-[var(--site-muted)]">
                  {data.description}
                </p>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
